import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FiHome, FiCalendar, FiUsers, FiTool, FiSettings, 
  FiBarChart2, FiFileText, 
  FiClock, FiTruck, FiBook, FiPlusCircle, FiMenu, FiX,
  FiLayers, FiUserPlus, FiChevronRight
} from 'react-icons/fi';

const Sidebar = ({ userRole }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Track window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const NavItem = ({ icon, label, to }) => {
    const active = isActive(to);
    return (
      <Link 
        to={to} 
        className={`flex items-center justify-between rounded-2xl px-4 py-3 transition-colors ${
          active 
            ? 'bg-primary text-white' 
            : 'text-slate-600 hover:bg-slate-100'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <div className="flex items-center space-x-3 truncate">
        {icon}
          <span className="truncate">{label}</span>
        </div>
        {!active && <FiChevronRight size={16} className="flex-shrink-0" />}
      </Link>
    );
  };

  const renderNavItems = () => {
    switch (userRole) {
      case 'owner':
        return (
          <>
            <NavItem 
              icon={<FiHome size={20} />} 
              label="Dashboard" 
              to="/owner/dashboard" 
            />
            <NavItem 
              icon={<FiLayers size={20} />} 
              label="Manajemen" 
              to="/owner/management" 
            />
            <NavItem 
              icon={<FiUsers size={20} />} 
              label="Struktur" 
              to="/owner/structure" 
            />
            <NavItem 
              icon={<FiUserPlus size={20} />} 
              label="Akun" 
              to="/owner/account-management" 
            />
          </>
        );
      case 'admin':
        return (
          <>
            <NavItem 
              icon={<FiHome size={20} />} 
              label="Dashboard" 
              to="/admin/dashboard" 
            />
            <NavItem 
              icon={<FiCalendar size={20} />} 
              label="Booking" 
              to="/admin/bookings" 
            />
            <NavItem 
              icon={<FiClock size={20} />} 
              label="Jadwal Booking" 
              to="/admin/booking-schedule" 
            />
            <NavItem 
              icon={<FiUsers size={20} />} 
              label="User" 
              to="/admin/users" 
            />
            <NavItem 
              icon={<FiTool size={20} />} 
              label="Montir" 
              to="/admin/mechanics" 
            />
            <NavItem 
              icon={<FiSettings size={20} />} 
              label="Layanan" 
              to="/admin/services" 
            />
            <NavItem 
              icon={<FiBarChart2 size={20} />} 
              label="Riwayat Transaksi" 
              to="/admin/transactions" 
            />
          </>
        );
      case 'staff':
        return (
          <>
            <NavItem 
              icon={<FiHome size={20} />} 
              label="Dashboard" 
              to="/staff/dashboard" 
            />
            <NavItem 
              icon={<FiFileText size={20} />} 
              label="Pembayaran" 
              to="/staff/pembayaran" 
            />
            <NavItem 
              icon={<FiBarChart2 size={20} />} 
              label="Riwayat Transaksi" 
              to="/staff/riwayat-transaksi" 
            />
          </>
        );
      case 'mechanic':
        return (
          <>
            <NavItem 
              icon={<FiHome size={20} />} 
              label="Dashboard" 
              to="/mechanic/dashboard" 
            />
            <NavItem 
              icon={<FiTruck size={20} />} 
              label="Pekerjaan" 
              to="/mechanic/jobs" 
            />
            <NavItem 
              icon={<FiClock size={20} />} 
              label="Jadwal" 
              to="/mechanic/schedule" 
            />
            <NavItem 
              icon={<FiFileText size={20} />} 
              label="Catatan Lapangan" 
              to="/mechanic/field-notes" 
            />
          </>
        );
      case 'customer':
        return (
          <>
            <NavItem 
              icon={<FiHome size={20} />} 
              label="Dashboard" 
              to="/customer/dashboard" 
            />
            <NavItem 
              icon={<FiCalendar size={20} />} 
              label="Booking Saya" 
              to="/customer/bookings" 
            />
            <NavItem 
              icon={<FiPlusCircle size={20} />} 
              label="Booking Baru" 
              to="/customer/book" 
            />
            <NavItem 
              icon={<FiBook size={20} />} 
              label="Katalog Layanan" 
              to="/customer/services" 
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Mobile Menu Toggle */}
      <div className="fixed left-4 top-[84px] z-40 lg:hidden">
        <button 
          onClick={toggleMobileMenu}
          className="rounded-full bg-white p-2 text-gray-700 shadow-md focus:outline-none"
          aria-label="Toggle sidebar menu"
        >
          {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </div>
      
      {/* Sidebar for Desktop */}
      <div className="fixed left-0 top-[72px] hidden h-[calc(100vh-72px)] w-72 overflow-hidden border-r border-slate-200 bg-white lg:block">
        <div className="flex h-full flex-col p-6">
          <div className="flex items-center mb-8">
            <span className="text-primary font-bold text-2xl mr-1">X</span>
            <span className="font-bold text-xl text-secondary">Signature</span>
          </div>
          <div className="space-y-2 overflow-y-auto scrollbar-hide flex-grow">
            {renderNavItems()}
          </div>
        </div>
      </div>
      
      {/* Mobile Sidebar */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={toggleMobileMenu}>
          <div 
            className="mt-[72px] h-[calc(100vh-72px)] w-72 overflow-y-auto bg-white shadow-lg transition-transform duration-300 scrollbar-hide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  <span className="text-primary font-bold text-2xl mr-1">X</span>
                  <span className="font-bold text-xl text-secondary">Signature</span>
                </div>
                <button 
                  onClick={toggleMobileMenu}
                  className="text-gray-500 focus:outline-none"
                  aria-label="Close sidebar menu"
                >
                  <FiX size={24} />
                </button>
              </div>
              <div className="space-y-2">
                {renderNavItems()}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar; 
